import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ErsGridComponent } from '@rcs/grid';
import type { ColDef, GridOptions } from 'ag-grid-enterprise';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, forkJoin } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ApiNotificationService } from '@rcs/notify';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { filter, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  SaveLeaveTypeInput,
  AccrualPolicyConfig,
  AccrualSimulationRow,
  AccrualMethod,
  ProrationBasis,
  RoundingMode,
} from '../../models/leave.model';
import { DeleteDialogComponent } from '../../dialogs/delete-dialog/delete-dialog.component';
import { DocPreviewDialogComponent, DocPreviewDialogData } from '@rcs/doc-viewer';
import { LeaveService } from '../../services/leave.service';
import { LmHttpAdapter } from '../../services/lm-http-adapter';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { parseLocalDate, toISODate } from '../../utils/date.utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { ErsSortByPipe } from '@rcs/ers-elementals';

/** Maps allocation frequency name (lowercase) to human-readable badge label. */
const FREQ_LABEL_BY_NAME: Record<string, (n: number) => string> = {
  'monthly':     (n) => `${Math.round(n / 12)} Monthly`,
  'quarterly':   (n) => `${Math.round(n / 4)} Quarterly`,
  'half yearly': (n) => `${Math.round(n / 2)} Half yearly`,
  'yearly':      (n) => `${n} Yearly`,
};

/** Maps allocation frequency name (lowercase) to number of allocation periods per year. */
const PERIODS_BY_NAME: Record<string, number> = {
  'monthly':     12,
  'quarterly':    4,
  'half yearly':  2,
  'yearly':       1,
};

/** Maps carry-forward select value to percentage sent to backend. */
const CARRY_PCT: Record<string, number | null> = {
  half:   50,
  full:   100,
  custom: null,
};

/** Maps carry-forward control value to a badge label. */
const CARRY_LABELS: Record<string, (n: number, pct?: number) => string> = {
  half:   (n) => `${Math.round(n / 2)} leaves carry forward`,
  full:   (n) => `${n} leaves carry forward`,
  custom: (n, pct) => pct ? `${Math.round(n * pct / 100)} leaves carry forward (${pct}%)` : 'Custom carry forward',
};

@Component({
  selector: 'ers-create-leave',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatDialogModule,
    MatAutocompleteModule,
    LmLoadingOverlayComponent,
    ErsSortByPipe,
    ErsGridComponent,
  ],
  templateUrl:'./create-leave.component.html',
  styleUrls:  ['./create-leave.component.scss'],
})
export class CreateLeaveComponent implements OnInit {

  form!: FormGroup;
  noteCharCount = 0;
  /** Each entry is either a pending new file (file set) or an already-saved doc (url set). */
  attachments: { file: File | null; name: string; url: string }[] = [];
  isUploading = false;
  isLoadingForm = false;
  isSaving = false;
  editingId: string | null = null;

  // ── Accrual simulation (PBI 34800) — edit mode only ────────────────────
  simulationRows: AccrualSimulationRow[] = [];
  isSimulating = false;
  hasSimulated = false;
  simAsOfDate: Date | null = null;
  simColumnDefs: ColDef[] = [
    { headerName: 'Period',  field: 'periodIndex', width: 90 },
    { headerName: 'From',    field: 'periodStart', width: 130, valueFormatter: p => this.fmtGridDate(p.value) },
    { headerName: 'To',      field: 'periodEnd',   width: 130, valueFormatter: p => this.fmtGridDate(p.value) },
    { headerName: 'Service (mo)', field: 'serviceMonths', width: 120, type: 'rightAligned' },
    { headerName: 'Base',    field: 'baseQty',     width: 110, type: 'rightAligned', valueFormatter: p => this.fmtGridNum(p.value) },
    { headerName: 'Prorated', field: 'proratedQty', width: 120, type: 'rightAligned', valueFormatter: p => this.fmtGridNum(p.value) },
    { headerName: 'Accrued', field: 'accruedQty',  width: 120, type: 'rightAligned', valueFormatter: p => this.fmtGridNum(p.value), cellStyle: { fontWeight: '600' } },
    { headerName: 'Running total', field: 'runningTotal', flex: 1, minWidth: 130, type: 'rightAligned', valueFormatter: p => this.fmtGridNum(p.value) },
  ];
  simGridOptions: Partial<GridOptions> = {
    suppressCellFocus: true,
    rowHeight: 38,
    headerHeight: 40,
    pagination: false,
    onGridReady: p => p.api.sizeColumnsToFit(),
  };

  private tenantId: string | null = null;
  private destroyRef = inject(DestroyRef);
  private isSyncingValidDates = false;

  /** Dynamically loaded allocation frequency options from SUMM_DATA. */
  allocationFrequencies: { id: string; name: string }[] = [];

  // Autocomplete
  predefinedLeaveTypes: string[] = [
    'Sick Leave',
    'Casual Leave',
    'Annual Leave',
    'Maternity Leave',
  ];
  filteredLeaveTypes: Observable<string[]> = new Observable();

  private fb         = inject(FormBuilder);
  private router     = inject(Router);
  private route      = inject(ActivatedRoute);
  private leaveService = inject(LeaveService);
  private adapter      = inject(LmHttpAdapter);
  private dialog            = inject(MatDialog);
  private notify             = inject(ApiNotificationService);
  get canViewGiftOfTime(): boolean         { return false; }

  /** Resolve periods-per-year from a frequency ID by looking up the loaded frequencies. */
  private getPeriodsPerYear(freqId: string): number {
    const freq = this.allocationFrequencies.find(f => f.id.toLowerCase() === freqId?.toLowerCase());
    return freq ? (PERIODS_BY_NAME[freq.name.toLowerCase()] ?? 1) : 1;
  }


  constructor() {}

  ngOnInit(): void {
    // Prefetch tenant id
    this.adapter.selectedUserTenant$.pipe(take(1)).subscribe(t => {
      if (t?.id) this.tenantId = t.id;
    });

    // Load allocation frequencies from UM MasterData
    this.adapter.get<{ id: string; dataValue: string }[]>('dropdowns', [{ type: 'LEAVE_TYPE_ALLOCATION_FREQUENCY' }])
      .pipe(take(1))
      .subscribe({
        next: (items) => {
          this.allocationFrequencies = (items || []).map(i => ({ id: i.id, name: i.dataValue }));
          // Set default for create mode; for edit mode, loadEditData will set the value
          if (!this.editingId && this.allocationFrequencies.length) {
            const halfYearly = this.allocationFrequencies.find(f => f.name.toLowerCase() === 'half yearly');
            this.form.get('frequencyOfAllocation')?.setValue((halfYearly ?? this.allocationFrequencies[0]).id.toLowerCase());
          }
          // Load edit data after frequencies are available (so getPeriodsPerYear works)
          if (this.editingId) {
            this.loadEditData();
          }
        },
        error: (err) => console.error('Failed to load allocation frequencies', err),
      });

    this.form = this.fb.group({
      leaveType:                   ['', Validators.required],
      isActive:                    [true],
      leavePeriodStart:            [null],
      requiresSupervisorApproval:  [true],

      // Gift of Time
      giftOfTime:         [false],
      maxGiftDays:        [1],
      giftExpiryDate:     [null],
      transferLimit:      [1],
      giftReasonRequired: [false],

      // Allocation
      frequencyOfAllocation: [null, Validators.required],
      leavesPerYear:         [12, [Validators.required, Validators.min(1)]],

      // Valid Till
      validStart: [null],
      duration:   [1],
      validEnd:   [null],

      // Carry Forward
      carryForward:          [true],
      carryForwardCount:     ['half'],
      customCarryPct:        [null],
      carryExpiryDate:       [null],

      // Accrual Policy (PBI 34800)
      accrualMethod:      ['PER_PERIOD'],
      accrualRatePerHour: [null],
      prorateFirstPeriod: [true],
      prorationBasis:     ['PERIOD_START'],
      roundingMode:       ['NONE'],
      roundingIncrement:  [null],
      tenureTiers:        this.fb.array([]),

      // Notes
      notes: ['', Validators.maxLength(100)],
    });

    // ── Conditional validators ─────────────────────────────────────────────
    // giftOfTime toggle → maxGiftDays required + min(1)
    this.form.get('giftOfTime')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((on: boolean) => {
        const ctrl = this.form.get('maxGiftDays')!;
        if (on) {
          ctrl.setValidators([Validators.required, Validators.min(1)]);
        } else {
          ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity();
      });

    // carryForward toggle → carryForwardCount required
    this.form.get('carryForward')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((on: boolean) => {
        const ctrl = this.form.get('carryForwardCount')!;
        if (on) {
          ctrl.setValidators([Validators.required]);
        } else {
          ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity();
        // Also clear custom pct validators when carry forward is off
        if (!on) {
          const pctCtrl = this.form.get('customCarryPct')!;
          pctCtrl.clearValidators();
          pctCtrl.updateValueAndValidity();
        }
      });

    // carryForwardCount → customCarryPct validators
    this.form.get('carryForwardCount')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val: string) => {
        const pctCtrl = this.form.get('customCarryPct')!;
        if (val === 'custom') {
          pctCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
        } else {
          pctCtrl.clearValidators();
          pctCtrl.setValue(null);
        }
        pctCtrl.updateValueAndValidity();
      });

    // Populate form if editing an existing leave type
    this.editingId = this.route.snapshot.paramMap.get('id');

    // Sync validStart/duration <-> validEnd
    const calcDurationFromDates = (start: Date, end: Date): number => {
      const monthDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      const endMonthDays = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
      const includesEndMonth = end.getDate() >= start.getDate() || end.getDate() === endMonthDays;
      return Math.max(1, monthDiff + (includesEndMonth ? 1 : 0));
    };
    const calcEndFromStartAndDuration = (start: Date, duration: number): Date => {
      const end = new Date(start);
      end.setMonth(end.getMonth() + Math.max(1, duration));
      end.setDate(end.getDate() - 1); // last day of the final month
      return end;
    };

    this.form.get('validStart')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((startVal: Date | null) => {
        if (this.isSyncingValidDates) return;
        const duration = this.form.get('duration')?.value as number | null;
        if (!startVal || !duration) return;
        this.isSyncingValidDates = true;
        this.form.get('validEnd')?.setValue(calcEndFromStartAndDuration(new Date(startVal), duration), { emitEvent: false });
        this.isSyncingValidDates = false;
      });

    this.form.get('duration')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((durationVal: number | null) => {
        if (this.isSyncingValidDates) return;
        const startVal = this.form.get('validStart')?.value as Date | null;
        if (!startVal || !durationVal) return;
        this.isSyncingValidDates = true;
        this.form.get('validEnd')?.setValue(calcEndFromStartAndDuration(new Date(startVal), durationVal), { emitEvent: false });
        this.isSyncingValidDates = false;
      });

    // Setup leave type autocomplete filter
    this.filteredLeaveTypes = this.form.get('leaveType')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterLeaveTypes(value)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private _filterLeaveTypes(value: string): string[] {
    const filterValue = (value || '').toLowerCase();
    return this.predefinedLeaveTypes.filter(type =>
      type.toLowerCase().includes(filterValue)
    );
  }

  // ── Computed badge labels ──────────────────────────────────────────────

  get frequencyBadge(): string {
    const freq  = this.form.get('frequencyOfAllocation')?.value as string;
    const count = this.form.get('leavesPerYear')?.value as number;
    const match = this.allocationFrequencies.find(f => f.id.toLowerCase() === freq?.toLowerCase());
    const fn    = match ? FREQ_LABEL_BY_NAME[match.name.toLowerCase()] : undefined;
    return fn ? fn(count ?? 0) : '';
  }

  get carryForwardBadge(): string {
    const type  = this.form.get('carryForwardCount')?.value as string;
    const count = this.form.get('leavesPerYear')?.value as number;
    const pct   = this.form.get('customCarryPct')?.value as number;
    const fn    = CARRY_LABELS[type];
    return fn ? fn(count ?? 0, pct) : '';
  }

  // ── Load policy documents for leave type ──────────────────────────────

  private loadPolicyDocumentsForLeaveType(leaveTypeId: string): void {
    this.leaveService.getPolicyDocuments().subscribe({
      next: (docs) => {
        // Filter documents for this leave type
        const idLower = leaveTypeId.toLowerCase();
        const leaveTypeDocs = docs.filter(d => {
          const docId = (d as any).leaveTypeId ?? (d as any).LEAVE_TYPE_ID;
          return docId && String(docId).toLowerCase() === idLower;
        });
        
        this.attachments = leaveTypeDocs.map(doc => ({
          file: null,
          name: doc.documentTitle || 'document',
          url:  doc.documentUrl  || (doc as any).documentPath || '',
        }));
      },
      error: (err) => console.error('Failed to load policy documents', err),
    });
  }

  // ── Load existing leave type for edit mode ────────────────────────────

  private loadEditData(): void {
    this.isLoadingForm = true;
    this.leaveService.getAllLeaveTypesManagement().subscribe(types => {
      this.isLoadingForm = false;
      const type = types.find(t => String(t.id) === this.editingId);
      if (type) {
        // Reverse-map percentageCarriedForward back to the select options
        let carryForwardCount = 'half';
        if (type.percentageCarriedForward === 100) carryForwardCount = 'full';
        else if (type.percentageCarriedForward != null && type.percentageCarriedForward !== 50) carryForwardCount = 'custom';

        // Determine duration based on numberOfMonthsValidTo
        const duration = type.numberOfMonthsValidTo ?? 1;

        // Compute validEnd from leavePeriodStartDate + duration
        const periodStart = type.leavePeriodStartDate ? parseLocalDate(type.leavePeriodStartDate) : null;
        const computedEnd = (periodStart && duration)
          ? (() => { const e = new Date(periodStart); e.setMonth(e.getMonth() + duration); e.setDate(e.getDate() - 1); return e; })()
          : null;

        // Disable sync callbacks while bulk-patching
        this.isSyncingValidDates = true;
        this.form.patchValue({
          leaveType:                  type.leaveTypeName,
          isActive:                   type.isActive !== false,
          requiresSupervisorApproval: type.isApprovalRequired ?? true,
          frequencyOfAllocation:      type.allocationFrequencyId?.toLowerCase(),
          leavesPerYear:              type.quantityAllocatedForEachPeriod * this.getPeriodsPerYear(type.allocationFrequencyId),
          carryForward:               type.isCarriedForward,
          carryForwardCount,
          customCarryPct:             carryForwardCount === 'custom' ? type.percentageCarriedForward : null,
          carryExpiryDate:            type.carryForwardExpiryDate
                                        ? parseLocalDate(type.carryForwardExpiryDate)
                                        : null,
          leavePeriodStart:           periodStart,
          validStart:                 periodStart,
          duration,
          validEnd:                   computedEnd,
          notes:                      type.notes ?? '',
          // Gift of Time
          giftOfTime:                 type.isGiftingLeaveAllowed ?? false,
          maxGiftDays:                type.maximumDaysAllowedToGift ?? 1,
          transferLimit:              type.maximumLeavesThatCanBeAcceptedAsGift ?? 1,
          giftReasonRequired:         type.isGiftReasonRequired ?? false,
          // Accrual Policy (PBI 34800) — NULL falls back to the default
          accrualMethod:              type.accrualMethod ?? 'PER_PERIOD',
          accrualRatePerHour:         type.accrualRatePerHour ?? null,
          prorateFirstPeriod:         type.prorateFirstPeriod ?? true,
          prorationBasis:             type.prorationBasis ?? 'PERIOD_START',
          roundingMode:               type.roundingMode ?? 'NONE',
          roundingIncrement:          type.roundingIncrement ?? null,
        });

        // Tenure tiers live in their own table — load and populate the array.
        this.tenureTiers.clear();
        this.leaveService.getTenureTiers(this.editingId!).subscribe(tiers => {
          this.tenureTiers.clear();
          (tiers ?? []).forEach(t => this.tenureTiers.push(
            this.newTierGroup(t.minMonthsOfService, t.quantityPerPeriod),
          ));
        });
        this.isSyncingValidDates = false;

        // Trigger conditional validators to match loaded state
        this.form.get('giftOfTime')!.updateValueAndValidity({ emitEvent: true });
        this.form.get('carryForward')!.updateValueAndValidity({ emitEvent: true });

        // Load policy documents for this leave type
        if (this.editingId) {
          this.loadPolicyDocumentsForLeaveType(this.editingId);
        }
      }
    });
  }

  // ── Event handlers ────────────────────────────────────────────────────

  onNotesInput(): void {
    this.noteCharCount = (this.form.get('notes')?.value ?? '').length;
  }

  onFileSelected(event: Event): void {
    const files = Array.from((event.target as HTMLInputElement).files ?? []);
    // Reset input so the same file can be re-selected if removed
    (event.target as HTMLInputElement).value = '';
    for (const file of files) {
      // Skip duplicates already in the list
      if (!this.attachments.some(a => a.name === file.name && a.file)) {
        this.attachments = [...this.attachments, { file, name: file.name, url: '' }];
      }
    }
  }

  removeAttachment(index: number): void {
    this.attachments = this.attachments.filter((_, i) => i !== index);
  }

  previewAttachment(index: number): void {
    const att = this.attachments[index];
    if (!att?.url) return;
    this.dialog.open<DocPreviewDialogComponent, DocPreviewDialogData>(
      DocPreviewDialogComponent,
      { data: { src: att.url, fileName: att.name || 'Document' }, maxWidth: '95vw', panelClass: 'lm-dialog-panel' }
    );
  }

  downloadAttachment(index: number): void {
    const att = this.attachments[index];
    if (!att?.url) return;
    this.leaveService.downloadProxiedDocument(att.url).subscribe({
      next: (blob) => this.triggerBlobDownload(blob, att.name || 'document'),
      error: () => window.open(att.url, '_blank', 'noopener,noreferrer'),
    });
  }

  triggerBlobDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const anchor  = document.createElement('a');
    anchor.href     = blobUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => {
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  }

  private uploadFileIfNeeded(): void {
    const pending = this.attachments.filter(a => !!a.file);
    if (pending.length === 0) {
      this.saveLeaveType();
      return;
    }

    this.isUploading = true;
    const tenantId$ = this.tenantId
      ? of(this.tenantId)
      : this.adapter.selectedUserTenant$.pipe(
          filter(t => !!t?.id),
          map(t => t!.id),
          take(1),
        );

    tenantId$.pipe(
      switchMap(tenantId =>
        forkJoin(pending.map(att =>
          this.adapter.uploadDocument(att.file!, tenantId, 'leaveDocuments').pipe(
            map(res => ({ name: att.name, url: res.fileUrl ?? '' })),
          )
        ))
      ),
    ).subscribe({
      next: (uploaded) => {
        // Merge uploaded URLs back into attachments array
        const urlMap = new Map(uploaded.map(u => [u.name, u.url]));
        this.attachments = this.attachments.map(a =>
          a.file && urlMap.has(a.name) ? { file: null, name: a.name, url: urlMap.get(a.name)! } : a
        );
        this.isUploading = false;
        this.saveLeaveTypeAndDocument();
      },
      error: (err) => {
        console.error('File upload failed', err);
        this.isUploading = false;
        this.saveLeaveType();
      },
    });
  }

  onReset(): void {
    this.form.reset({
      isActive:              true,
      requiresSupervisorApproval: true,
      giftOfTime:            false,
      carryForward:          true,
      frequencyOfAllocation: (this.allocationFrequencies.find(f => f.name.toLowerCase() === 'half yearly') ?? this.allocationFrequencies[0])?.id?.toLowerCase() ?? null,
      leavesPerYear:         12,
      duration:              1,
      maxGiftDays:           1,
      transferLimit:         1,
    });
    this.noteCharCount = 0;
    this.attachments   = [];
  }

  onDelete(): void {
    if (!this.editingId) {
      this.navigateBack();
      return;
    }

    const leaveTypeName = this.form.get('leaveType')?.value || 'this leave type';
    this.dialog.open(DeleteDialogComponent, {
      width: '460px',
      panelClass: 'lm-dialog-panel',
      data: { itemName: leaveTypeName },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isSaving = true;
      this.leaveService.deleteLeaveType(this.editingId!).subscribe({
        next: () => { this.isSaving = false; this.navigateBack(); },
        error: () => { this.isSaving = false; },
      });
    });
  }

  onCreate(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.uploadFileIfNeeded();
  }

  private saveLeaveType(): void {
    const v = this.form.value;

    // Map carry-forward select ('half'|'full'|'custom') to a numeric percentage
    const percentageCarriedForward: number | null = v.carryForward
      ? (v.carryForwardCount === 'custom' ? (v.customCarryPct ?? null) : (CARRY_PCT[v.carryForwardCount as string] ?? null))
      : null;

    // Format dates to ISO string if provided
    const fmtDate = (d: Date | null | undefined): string | null =>
      d ? toISODate(d) : null;
    const validStart = v.validStart ? new Date(v.validStart) : null;
    const validEnd = v.validEnd ? new Date(v.validEnd) : null;
    const durationFromDates = (validStart && validEnd)
      ? (() => {
          const md = (validEnd.getFullYear() - validStart.getFullYear()) * 12 + (validEnd.getMonth() - validStart.getMonth());
          const edm = new Date(validEnd.getFullYear(), validEnd.getMonth() + 1, 0).getDate();
          return Math.max(1, md + (validEnd.getDate() >= validStart.getDate() || validEnd.getDate() === edm ? 1 : 0));
        })()
      : null;
    const leavePeriodStartDate = fmtDate(validStart ?? v.leavePeriodStart);
    const numberOfMonthsValidTo = durationFromDates ?? v.duration ?? null;
    const periodsPerYear = this.getPeriodsPerYear(v.frequencyOfAllocation);

    const dto: SaveLeaveTypeInput = {
      leaveTypeName:                          v.leaveType,
      isActive:                               v.isActive ?? true,
      isApprovalRequired:                     v.requiresSupervisorApproval ?? true,
      notes:                                  v.notes?.trim() ? v.notes.trim() : null,
      allocationFrequencyId:                  v.frequencyOfAllocation,
      quantityAllocatedForEachPeriod:         Math.round(v.leavesPerYear / periodsPerYear),
      isCarriedForward:                       v.carryForward ?? false,
      percentageCarriedForward,
      carryForwardExpiryDate:                 fmtDate(v.carryExpiryDate),
      leavePeriodStartDate,
      numberOfMonthsValidTo,
      isCountedInclusiveOfOtherHolidays:      false,
      isAllocatedAtStartOfPeriod:             false,
      isSuspendedDuringNoticePeriod:          false,
      isEncashable:                           false,
      isEncashableAboveMaximumAccumulation:   false,
      isGiftingLeaveAllowed:                  v.giftOfTime ?? false,
      maximumDaysAllowedToGift:               v.giftOfTime ? (v.maxGiftDays ?? null) : null,
      isGiftReasonRequired:                   v.giftOfTime ? (v.giftReasonRequired ?? false) : false,
      isGiftedLeaveReturnedAfterExpiry:       false,
      ...this.buildAccrualConfig(v),
    };

    if (this.editingId) {
      this.leaveService.updateLeaveType({ ...dto, id: this.editingId }).subscribe({
        next: (res) => {
          this.isSaving = false;
          if ((res as any)?.success === false) {
            this.notify.addError((res as any).message || 'Update failed. Please try again.');
            return;
          }
          this.navigateBack();
        },
        error: (err) => {
          this.isSaving = false;
          const msg = err?.error?.message || err?.message || 'Update failed. Please try again.';
          this.notify.addError(msg);
        },
      });
    } else {
      this.leaveService.createLeaveType(dto).subscribe({
        next: (res) => {
          this.isSaving = false;
          if ((res as any)?.success === false) {
            this.notify.addError((res as any).message || 'Create failed. Please try again.');
            return;
          }
          this.navigateBack();
        },
        error: (err) => {
          this.isSaving = false;
          const msg = err?.error?.message || err?.message || 'Create failed. Please try again.';
          this.notify.addError(msg);
        },
      });
    }
  }

  private saveLeaveTypeAndDocument(): void {
    const v = this.form.value;

    // Map carry-forward select ('half'|'full'|'custom') to a numeric percentage
    const percentageCarriedForward: number | null = v.carryForward
      ? (v.carryForwardCount === 'custom' ? (v.customCarryPct ?? null) : (CARRY_PCT[v.carryForwardCount as string] ?? null))
      : null;

    // Format dates to ISO string if provided
    const fmtDate = (d: Date | null | undefined): string | null =>
      d ? toISODate(d) : null;
    const validStart = v.validStart ? new Date(v.validStart) : null;
    const validEnd = v.validEnd ? new Date(v.validEnd) : null;
    const durationFromDates = (validStart && validEnd)
      ? (() => {
          const md = (validEnd.getFullYear() - validStart.getFullYear()) * 12 + (validEnd.getMonth() - validStart.getMonth());
          const edm = new Date(validEnd.getFullYear(), validEnd.getMonth() + 1, 0).getDate();
          return Math.max(1, md + (validEnd.getDate() >= validStart.getDate() || validEnd.getDate() === edm ? 1 : 0));
        })()
      : null;
    const leavePeriodStartDate = fmtDate(validStart ?? v.leavePeriodStart);
    const numberOfMonthsValidTo = durationFromDates ?? v.duration ?? null;
    const periodsPerYear = this.getPeriodsPerYear(v.frequencyOfAllocation);

    const dto: SaveLeaveTypeInput = {
      leaveTypeName:                          v.leaveType,
      isActive:                               v.isActive ?? true,
      isApprovalRequired:                     v.requiresSupervisorApproval ?? true,
      notes:                                  v.notes?.trim() ? v.notes.trim() : null,
      allocationFrequencyId:                  v.frequencyOfAllocation,
      quantityAllocatedForEachPeriod:         Math.round(v.leavesPerYear / periodsPerYear),
      isCarriedForward:                       v.carryForward ?? false,
      percentageCarriedForward,
      carryForwardExpiryDate:                 fmtDate(v.carryExpiryDate),
      leavePeriodStartDate,
      numberOfMonthsValidTo,
      isCountedInclusiveOfOtherHolidays:      false,
      isAllocatedAtStartOfPeriod:             false,
      isSuspendedDuringNoticePeriod:          false,
      isEncashable:                           false,
      isEncashableAboveMaximumAccumulation:   false,
      isGiftingLeaveAllowed:                  v.giftOfTime ?? false,
      maximumDaysAllowedToGift:               v.giftOfTime ? (v.maxGiftDays ?? null) : null,
      isGiftReasonRequired:                   v.giftOfTime ? (v.giftReasonRequired ?? false) : false,
      isGiftedLeaveReturnedAfterExpiry:       false,
      ...this.buildAccrualConfig(v),
    };

    const saveObs = this.editingId
      ? this.leaveService.updateLeaveType({ ...dto, id: this.editingId })
      : this.leaveService.createLeaveType(dto);

    saveObs.subscribe({
      next: (result) => {
        if ((result as any)?.success === false) {
          this.isSaving = false;
          this.notify.addError((result as any).message || 'Save failed. Please try again.');
          return;
        }
        const docsWithUrl = this.attachments.filter(a => !!a.url);
        if (docsWithUrl.length === 0) {
          this.navigateBack();
          return;
        }
        const leaveTypeId = this.editingId || (result as any).id || null;
        forkJoin(
          docsWithUrl.map(att =>
            this.leaveService.savePolicyDocument({
              documentTitle: att.name || 'document',
              documentUrl:   att.url,
              leaveTypeId,
            })
          )
        ).subscribe({
          next:  () => { this.isSaving = false; this.navigateBack(); },
          error: (err) => { this.isSaving = false; console.error('Failed to save policy documents', err); this.navigateBack(); },
        });
      },
      error: (err) => {
        this.isSaving = false;
        const msg = err?.error?.message || err?.message || 'Save failed. Please try again.';
        this.notify.addError(msg);
      },
    });
  }

  // ── Accrual Policy (PBI 34800) ─────────────────────────────────────────

  get tenureTiers(): FormArray {
    return this.form.get('tenureTiers') as FormArray;
  }

  private newTierGroup(minMonths = 0, qty: number | null = null): FormGroup {
    return this.fb.group({
      minMonthsOfService: [minMonths, [Validators.required, Validators.min(0)]],
      quantityPerPeriod:  [qty, [Validators.required, Validators.min(0)]],
    });
  }

  addTier(): void {
    this.tenureTiers.push(this.newTierGroup());
  }

  removeTier(index: number): void {
    this.tenureTiers.removeAt(index);
  }

  /** The accrual-config subset of the save payload (shared by both DTO builders). */
  private buildAccrualConfig(v: any): AccrualPolicyConfig {
    const method = (v.accrualMethod ?? 'PER_PERIOD') as AccrualMethod;
    const roundingMode = (v.roundingMode ?? 'NONE') as RoundingMode;
    const tiers = ((v.tenureTiers ?? []) as any[])
      .filter(t => t && t.quantityPerPeriod != null && t.quantityPerPeriod !== '')
      .map(t => ({
        minMonthsOfService: Number(t.minMonthsOfService ?? 0),
        quantityPerPeriod:  Number(t.quantityPerPeriod),
      }));
    const hasRate = v.accrualRatePerHour != null && v.accrualRatePerHour !== '';
    const hasIncrement = v.roundingIncrement != null && v.roundingIncrement !== '';
    return {
      accrualMethod:      method,
      accrualRatePerHour: method === 'PER_HOURS_WORKED' && hasRate ? Number(v.accrualRatePerHour) : null,
      prorateFirstPeriod: v.prorateFirstPeriod ?? true,
      prorationBasis:     (v.prorationBasis ?? 'PERIOD_START') as ProrationBasis,
      roundingMode,
      roundingIncrement:  roundingMode !== 'NONE' && hasIncrement ? Number(v.roundingIncrement) : null,
      tenureTiers:        tiers,
    };
  }

  private fmtGridDate(value: unknown): string {
    if (!value) return '';
    const d = parseLocalDate(value as string);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  }

  private fmtGridNum(value: unknown): string {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '';
  }

  /** Preview what this saved policy would accrue for a sample employee. */
  runSimulation(): void {
    if (!this.editingId) return;
    this.isSimulating = true;
    this.leaveService.simulateAccrual({
      leaveTypeId: this.editingId,
      asOfDate: this.simAsOfDate ? toISODate(new Date(this.simAsOfDate)) : null,
    }).subscribe({
      next: (rows) => {
        this.simulationRows = rows ?? [];
        this.hasSimulated = true;
        this.isSimulating = false;
      },
      error: () => { this.isSimulating = false; },
    });
  }

  navigateBack(): void {
    this.router.navigate(['/ers/leave-management/manage-leave-policies']);
  }
}
